import { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { homeGroups as groups } from '@barter/shared';
import type { AdCondition } from '@barter/shared';
import { useAds } from '../../hooks/useAds';
import { useSnapshotProgress } from '../../hooks/useSnapshotProgress';
import { useUser } from '../../context/UserContext';
import { AdCard } from '../../components/AdCard/AdCard';
import { EmptyState } from '../../components/EmptyState/EmptyState';
import { AdSkeletonList } from '../../components/Skeleton/Skeleton';
import { Icon } from '../../components/Icon/Icon';
import { CitySelect } from '../../components/CitySelect/CitySelect';
import { refreshSnapshot } from '../../services/snapshot';
import { getDistinctMakes, getDistinctCities, type CityOption } from '../../db/database';

// Главная в стиле Google: логотип, строка поиска и кнопки — «Шукати»
// (уход на страницу поиска с запросом и фильтрами), «Фільтрація» (только в
// полном режиме) и «Розділи». Ниже — кнопки ниши (китайские авто / электромобили /
// Nissan Leaf) и переключатель «Повний режим». Свежие объявления остаются внизу.
export function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { categoryStyle, uiMode, setUiMode } = useUser();
  const snapshot = useSnapshotProgress();

  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set);

  // Поля фильтрации: применяются при «Шукати» — уходят в URL страницы поиска
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [city, setCity] = useState('');
  const [condition, setCondition] = useState<AdCondition | ''>('');
  const [category, setCategory] = useState('');
  const [makes, setMakes] = useState<string[]>([]);
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);

  useEffect(() => {
    let alive = true;
    const load = () => {
      getDistinctMakes()
        .then((list) => {
          if (alive) setMakes(list);
        })
        .catch(() => {});
      getDistinctCities()
        .then((list) => {
          if (alive) setCityOptions(list);
        })
        .catch(() => {});
    };
    load();
    window.addEventListener('barter:new-ads', load);
    return () => {
      alive = false;
      window.removeEventListener('barter:new-ads', load);
    };
  }, []);

  const { items, loading, error, refresh } = useAds({ status: 'active' });

  const openGroup = (id: string) => {
    navigate(`/search?category=${id}`);
  };

  const openFocus = (params: string) => {
    navigate(`/search?${params}`);
  };

  const submitSearch = () => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('search', query.trim());
    if (category) params.set('category', category);
    if (make) params.set('make', make);
    if (model.trim()) params.set('model', model.trim());
    if (city) params.set('city', city);
    if (condition) params.set('condition', condition);
    navigate(`/search?${params.toString()}`);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [pulling, setPulling] = useState(false);

  const PULL_THRESHOLD = 60;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const el = containerRef.current;
    if (!el || el.scrollTop > 0) return;
    startYRef.current = e.touches[0].clientY;
    setPulling(true);
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!pulling) return;
      const diff = e.touches[0].clientY - startYRef.current;
      if (diff <= 0) {
        setPullDistance(0);
        return;
      }
      const el = containerRef.current;
      if (el && el.scrollTop > 0) {
        setPullDistance(0);
        return;
      }
      setPullDistance(Math.min(diff * 0.5, PULL_THRESHOLD + 20));
    },
    [pulling],
  );

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD) {
      // Pull-to-refresh: сначала проверить версию снапшота, потом перечитать Dexie
      refreshSnapshot().finally(() => refresh());
    }
    setPullDistance(0);
    setPulling(false);
  }, [pullDistance, refresh]);

  const catalogLoading = snapshot.active && snapshot.firstLoad && items.length === 0;
  const catalogFailed = snapshot.failed && !snapshot.active && items.length === 0;
  const showSkeleton = (catalogLoading || (loading && items.length === 0)) && !catalogFailed;

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="pull-indicator"
        style={{
          height: pullDistance > 0 ? `${pullDistance}px` : 0,
          opacity: pullDistance > 0 ? Math.min(pullDistance / PULL_THRESHOLD, 1) : 0,
        }}
      >
        <Icon name={pulling && pullDistance >= PULL_THRESHOLD ? 'refresh' : 'chevron-down'} size={18} />
        <span className="pull-indicator__text">
          {pulling && pullDistance >= PULL_THRESHOLD
            ? t('home.releaseToRefresh')
            : t('home.pullToRefresh')}
        </span>
      </div>

      <section className="home-search">
        <span className="home-search__logo">
          <Icon name="swap" size={34} />
          {t('app.title')}
        </span>
        <span className="home-search__tagline">{t('home.tagline')}</span>
        <div className="home-search__bar">
          <span className="home-search__icon">
            <Icon name="search" size={20} />
          </span>
          <input
            className="home-search__input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
            placeholder={t('search.placeholder')}
            aria-label={t('nav.search')}
          />
          {query && (
            <button className="home-search__clear" onClick={() => setQuery('')} aria-label="Clear">
              <Icon name="x" size={16} />
            </button>
          )}
        </div>
        <div className="home-search__actions">
          <button type="button" className="home-search__btn" onClick={submitSearch}>
            {t('home.btnSearch')}
          </button>
          {uiMode === 'full' && (
            <button
              type="button"
              className={`home-search__btn${showFilters ? ' home-search__btn--active' : ''}`}
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
            >
              {t('home.btnFilters')}
            </button>
          )}
          <button
            type="button"
            className={`home-search__btn${showSections ? ' home-search__btn--active' : ''}`}
            onClick={() => setShowSections((v) => !v)}
            aria-expanded={showSections}
          >
            {t('home.btnSections')}
          </button>
        </div>
        <div className="home-focus">
          <button
            type="button"
            className="home-focus__btn"
            onClick={() => openFocus('focus=cn')}
          >
            <span className="home-focus__icon">🇨🇳</span>
            {t('home.focusCn')}
          </button>
          <button
            type="button"
            className="home-focus__btn"
            onClick={() => openFocus('focus=ev')}
          >
            <span className="home-focus__icon">🔋</span>
            {t('home.focusEv')}
          </button>
          <button
            type="button"
            className="home-focus__btn home-focus__btn--leaf"
            onClick={() => openFocus('make=Nissan&model=Leaf')}
          >
            <span className="home-focus__icon">🍃</span>
            Nissan Leaf
          </button>
        </div>
        <div className="home-mode">
          <span className="home-mode__label">
            {t('home.fullMode')}
            <span className="home-mode__hint">{t('home.fullModeHint')}</span>
          </span>
          <label className="subs-toggle">
            <input
              type="checkbox"
              checked={uiMode === 'full'}
              onChange={() => setUiMode(uiMode === 'full' ? 'simple' : 'full')}
              aria-label={t('home.fullMode')}
            />
            <span className="subs-toggle__slider" />
          </label>
        </div>
      </section>

      {uiMode === 'full' && showFilters && (
        <div className="home-filters-panel">
          <select
            className="form-select form-select--small"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label={t('home.categories')}
          >
            <option value="">{t('search.allCategories')}</option>
            {groups.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {t(cat.nameKey ?? cat.id)}
              </option>
            ))}
          </select>
          <div className="home-filters-panel__row">
            <select
              className="form-select form-select--small"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              aria-label={t('search.make')}
            >
              <option value="">{t('search.anyMake')}</option>
              {makes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <input
              className="form-input form-input--small"
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={t('search.model')}
              maxLength={60}
            />
          </div>
          <CitySelect
            value={city}
            onChange={setCity}
            placeholder={t('search.city')}
            suggestions={cityOptions}
            small
          />
          <div className="condition-toggle condition-toggle--filter">
            <button
              type="button"
              className={`condition-toggle__btn${!condition ? ' condition-toggle__btn--filter-active' : ''}`}
              onClick={() => setCondition('')}
            >
              {t('search.conditionAny')}
            </button>
            <button
              type="button"
              className={`condition-toggle__btn${condition === 'new' ? ' condition-toggle__btn--active-new' : ''}`}
              onClick={() => setCondition(condition === 'new' ? '' : 'new')}
            >
              {t('search.conditionNew')}
            </button>
            <button
              type="button"
              className={`condition-toggle__btn${condition === 'used' ? ' condition-toggle__btn--active-used' : ''}`}
              onClick={() => setCondition(condition === 'used' ? '' : 'used')}
            >
              {t('search.conditionUsed')}
            </button>
          </div>
        </div>
      )}

      {showSections && (
        <section className="home__categories">
          <h2 className="section-title">{t('home.categories')}</h2>
          <div className="home__categories-grid">
            {groups.map((cat) => {
              const hasPhoto = categoryStyle === 'photo' && !failedImages.has(cat.id);
              return (
                <button
                  key={cat.id}
                  className={`category-badge${hasPhoto ? ' category-badge--photo' : ''}`}
                  onClick={() => openGroup(cat.id)}
                >
                  {hasPhoto && (
                    <img
                      className="category-badge__img"
                      src={`/categories/${cat.id}.webp`}
                      alt=""
                      loading="lazy"
                      onError={() =>
                        setFailedImages((prev) => new Set(prev).add(cat.id))
                      }
                    />
                  )}
                  <span className="category-badge__icon">{cat.icon}</span>
                  <span className="category-badge__name">{t(cat.nameKey ?? cat.id)}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <div className="home-section__header">
          <h2 className="section-title">{t('home.latest')}</h2>
          <button className="home-section__more" onClick={() => navigate('/search')}>
            {t('home.viewAll')} <Icon name="arrow-right" size={15} />
          </button>
        </div>
        {catalogFailed && <div className="error-state">{t('common.snapshotError')}</div>}
        {error && <div className="error-state">{error}</div>}
        {showSkeleton && <AdSkeletonList count={3} />}
        {!loading && !error && items.length === 0 && !catalogLoading && !catalogFailed && (
          <EmptyState icon="📭" text={t('home.noAds')} hint={t('home.beFirst')} />
        )}
        <div className="offer-list">
          {items.map((ad) => (
            <AdCard key={ad.id} ad={ad} />
          ))}
        </div>
        {loading && items.length > 0 && <div className="loading-state">{t('home.loading')}</div>}
      </section>
    </div>
  );
}
